import { useRef, useState } from "react"
import { Eye, Loader2, Paperclip, Trash2, Upload } from "lucide-react"
import { useTranslation } from "react-i18next"

import { DatePopover } from "@/components/date-popover"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DocPreviewDialog } from "@/features/transit/components/doc-preview-dialog"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { StageDocGrid } from "@/features/transit/components/stage-doc-grid"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import {
  useDeleteSlotDoc,
  useUpsertSlotDoc,
} from "@/features/transit/hooks/use-doc-mutations"
import { usePatchOT } from "@/features/transit/hooks/use-transit-mutations"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { indexDocsBySlot } from "@/features/transit/lib/doc-slots"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import { cn } from "@/lib/utils"
import type { OT } from "@/features/transit/api/types"

interface RemisePanelProps {
  ot: OT
}

const COURIER_TRACKING_SLOT = "courierTrackingDoc" as const

// Remise paperwork-only stage. CADESA feedback added two header fields
// (handover date + courier tracking number). The courier tracking number
// also accepts an optional document (DHL/Fedex receipt) — rendered inline
// beside the input. The required REMISE doc slots remain in the grid
// below; the optional courier doc slot is excluded from it.
export function RemisePanel({ ot }: RemisePanelProps) {
  const { t } = useTranslation("transit")
  const locked = isOTLocked(ot)
  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <Card className="flex flex-col gap-4 p-5">
        <SectionTitle
          eyebrow={t("remise.eyebrow")}
          title={t("remise.title")}
          description={t("remise.description")}
        />
        <TrackingFields ot={ot} locked={locked} />
      </Card>
      <StageDocGrid ot={ot} stage="REMISE" exclude={[COURIER_TRACKING_SLOT]} />
    </div>
  )
}

function TrackingFields({ ot, locked }: { ot: OT; locked: boolean }) {
  const { t } = useTranslation("transit")
  const patch = usePatchOT(ot.id)
  const [courier, setCourier] = useState(ot.courierTrackingNumber ?? "")

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label>{t("remise.remiseDateLabel")}</Label>
        <DatePopover
          value={ot.remiseDate ?? ""}
          disabled={locked}
          onChange={(iso) => {
            if (iso !== (ot.remiseDate ?? "")) {
              patch.mutate({ remiseDate: iso })
            }
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>{t("remise.courierLabel")}</Label>
        <div className="flex items-center gap-2">
          <Input
            value={courier}
            placeholder={t("remise.courierPlaceholder")}
            disabled={locked}
            readOnly={locked}
            className={locked ? "disabled:opacity-100" : undefined}
            onChange={(e) => setCourier(e.target.value)}
            onBlur={() => {
              const next = courier.trim()
              if (next !== (ot.courierTrackingNumber ?? "")) {
                patch.mutate({ courierTrackingNumber: next || null })
              }
            }}
          />
          <CourierTrackingDocButton ot={ot} locked={locked} />
        </div>
      </div>
    </div>
  )
}

function CourierTrackingDocButton({ ot, locked }: { ot: OT; locked: boolean }) {
  const { t } = useTranslation("transit")
  const doc = indexDocsBySlot(ot.docs)[COURIER_TRACKING_SLOT]
  const upload = useTransitUpload()
  const upsert = useUpsertSlotDoc(ot.id)
  const remove = useDeleteSlotDoc(ot.id)
  const [previewOpen, setPreviewOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const meta = useUploadWithMetadata({
    kindLabel: t(`docLabels.${COURIER_TRACKING_SLOT}`, {
      defaultValue: COURIER_TRACKING_SLOT,
    }),
    upload: async (file, m) => {
      await upload.upload({
        otId: ot.id,
        kind: "slotDoc",
        slotId: COURIER_TRACKING_SLOT,
        file,
        register: async ({ storedKey, file: f }) =>
          upsert.mutateAsync({
            slotId: COURIER_TRACKING_SLOT,
            payload: {
              name: m.name,
              storedKey,
              docType: f.type || undefined,
              size: f.size,
              uploadedBy: m.uploadedBy || undefined,
              note: m.description || undefined,
            },
          }),
      })
    },
  })

  const isUploading = upload.isUploading || upsert.isPending
  const filled = !!doc

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) meta.onPick(file)
          if (fileInputRef.current) fileInputRef.current.value = ""
        }}
      />

      {filled ? (
        <div className="border-input bg-background flex h-9 max-w-[220px] flex-shrink-0 items-center gap-1 rounded-md border px-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs hover:underline"
              >
                <Paperclip className="text-muted-foreground size-3.5 flex-shrink-0" />
                <span className="truncate font-mono">{doc.name}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("preview.btn")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 flex-shrink-0"
                onClick={() => setPreviewOpen(true)}
                aria-label={t("preview.btn")}
              >
                <Eye className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("preview.btn")}</TooltipContent>
          </Tooltip>
          {locked ? null : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6 flex-shrink-0 hover:text-red-600"
                  onClick={() => remove.mutate(COURIER_TRACKING_SLOT)}
                  disabled={remove.isPending}
                  aria-label={t("docCard.delete")}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("docCard.replaceHint")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn("flex-shrink-0 gap-1.5", isUploading && "opacity-60")}
              disabled={isUploading || locked}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Upload className="size-3.5" />
              )}
              <span className="hidden sm:inline">
                {t("remise.courierDocUpload")}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("remise.courierDocUpload")}</TooltipContent>
        </Tooltip>
      )}

      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />

      <DocPreviewDialog
        otId={ot.id}
        doc={previewOpen && doc ? doc : null}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  )
}
